import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import { topMilhas, diasRestantes, urgencia } from '../data'

const destinos = ['Todos os destinos', 'Smiles', 'LATAM Pass', 'Azul'] as const
type Destino = (typeof destinos)[number]

const urgencyStyles = {
  critical: 'text-terracotta bg-terracotta/10 border-terracotta/30',
  warm: 'text-taupe-dark bg-taupe/15 border-taupe/40',
  steady: 'text-sage bg-sage/10 border-sage/30',
}

export function TopMilhas() {
  const [dest, setDest] = useState<Destino>('Todos os destinos')

  const ordered = useMemo(() => {
    const list = [...topMilhas]
    const filtered = dest === 'Todos os destinos' ? list : list.filter((m) => m.destino === dest)
    return filtered.sort((a, b) => b.percentual - a.percentual)
  }, [dest])

  return (
    <section id="milhas" className="bg-cream py-16 md:py-24">
      <div className="container-editorial">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-taupe" />
              <span className="text-xs tracking-[0.35em] uppercase text-taupe-dark font-medium">
                Melhores milhas
              </span>
            </div>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tighter text-ink">
              Bônus de <em className="text-taupe-dark">transferência ativos</em>.
            </h2>
            <p className="mt-3 text-base text-ink/60 max-w-xl">
              Quando o banco multiplica teus pontos na hora de virar milha.
              Ofertas vigentes agora, ordenadas pelo percentual.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {destinos.map((d) => (
              <button
                key={d}
                onClick={() => setDest(d)}
                aria-pressed={dest === d}
                className={`text-xs tracking-wide px-4 py-2 rounded-full transition-all ${
                  dest === d
                    ? 'bg-ink text-bone'
                    : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </header>

        <div className="grid gap-3 md:gap-4">
          {ordered.map((m, i) => {
            const dias = diasRestantes(m.validoAte)
            const urg = urgencia(dias)
            const hot = m.percentual >= 100

            return (
              <motion.a
                key={m.id}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className={`group grid md:grid-cols-[auto_160px_1fr_auto_auto] grid-cols-[1fr_auto] gap-4 md:gap-8 items-center rounded-2xl p-5 md:p-6 border transition-all hover:border-ink/40 ${
                  hot
                    ? 'bg-ink text-bone border-ink'
                    : 'bg-bone border-taupe/20'
                }`}
              >
                <div className={`hidden md:block font-sans font-light text-lg tabular-nums ${hot ? 'text-bone/50' : 'text-taupe-dark/60'}`}>
                  {String(i + 1).padStart(2, '0')}
                </div>

                <div>
                  <div className={`text-[10px] tracking-[0.3em] uppercase mb-1.5 ${hot ? 'text-bone/60' : 'text-taupe'}`}>
                    {m.origem} → {m.destino}
                  </div>
                  <div className={`font-sans font-semibold text-3xl md:text-4xl leading-none tabular-nums ${hot ? 'text-bone' : 'text-ink'}`}>
                    +{m.percentual}
                    <span className={`text-xl font-normal ml-0.5 ${hot ? 'text-bone/70' : 'text-taupe-dark'}`}>
                      %
                    </span>
                  </div>
                </div>

                <div className={`hidden md:block text-sm leading-relaxed ${hot ? 'text-bone/75' : 'text-ink/70'}`}>
                  {m.obs || (m.requerClube ? 'Bônus máximo exige Clube ativo.' : `Transferência ${m.origem} → ${m.destino}.`)}
                </div>

                <div className={`hidden md:flex flex-col items-end`}>
                  <span className={`inline-flex items-center text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full border ${
                    hot ? 'border-bone/30 text-bone/80 bg-bone/5' : urgencyStyles[urg]
                  }`}>
                    {dias <= 0 ? 'último dia' : `${dias} dia${dias !== 1 ? 's' : ''}`}
                  </span>
                  <span className={`text-xs mt-1 ${hot ? 'text-bone/50' : 'text-taupe'}`}>
                    até {new Date(m.validoAte + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                  </span>
                </div>

                {/* Mobile: compacto */}
                <div className="md:hidden text-right">
                  <div className={`text-xs ${hot ? 'text-bone/60' : 'text-taupe'}`}>
                    {dias}d · {new Date(m.validoAte + 'T00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                  </div>
                </div>

                <div className={`${hot ? 'text-bone/60 group-hover:text-bone' : 'text-ink/40 group-hover:text-ink'} transition-colors`}>
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                </div>
              </motion.a>
            )
          })}
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 mt-6 px-1">
          <p className="text-xs text-taupe">
            {ordered.length} {ordered.length === 1 ? 'bônus ativo' : 'bônus ativos'} · dados agregados de fontes públicas do mercado
          </p>
          <a href="#entrar" className="text-xs text-ink/70 hover:text-ink inline-flex items-center gap-1.5 group">
            Quero alerta quando aparecer 100%+
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}
